import { motion } from 'framer-motion'
import { ArrowLeft, Calendar, User, Tag } from 'lucide-react'
import { Link } from 'react-router-dom'

const RoboticsBlogPage = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-blue-50 to-purple-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-12"
          >
            <Link 
              to="/blogs" 
              className="inline-flex items-center text-primary-600 hover:text-primary-700 font-medium mb-6 transition-colors"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Blogs
            </Link>
            
            <div className="flex items-center justify-center space-x-4 mb-6 text-sm text-gray-600">
              <div className="flex items-center">
                <Calendar className="h-4 w-4 mr-1" />
                Dec 15, 2024
              </div>
              <div className="flex items-center">
                <User className="h-4 w-4 mr-1" />
                STEM Bears Team
              </div>
              <div className="flex items-center">
                <Tag className="h-4 w-4 mr-1" />
                Robotics
              </div>
            </div>

            <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              Building Your First Robot: A Step-by-Step Guide
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Learn how kids can build their very own robot from scratch using simple electronics, sensors, and coding concepts —no prior experience needed.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose prose-lg max-w-none">
            
            {/* Introduction */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="mb-12"
            >
              <p className="text-xl text-gray-700 leading-relaxed mb-8">
                At STEM Bears, we believe learning robotics isn't just about building machines — it's about building confidence, creativity, and curiosity. When kids build a robot, they discover how science, coding, and imagination come together to bring ideas to life.
              </p>
            </motion.div>

            {/* Video Section */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
              className="mb-16"
            >
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <video
                  src="/videos/Robotics-Blog.mp4"
                  className="w-full h-auto"
                  controls
                  poster="/videos/Robotics-Blog-Cover.mp4"
                >
                  Your browser does not support the video tag.
                </video>
              </div>
            </motion.div>

            {/* Step 1 */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              viewport={{ once: true }}
              className="mb-12"
            >
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Step 1: Understanding What a Robot Is</h2>
              <p className="text-lg text-gray-700 mb-6">
                A robot is a machine that can sense, think, and act. That means it can take input (like sound or movement), process it (through a small computer called a microcontroller), and perform actions (like moving wheels or lighting LEDs).
              </p>
              <p className="text-lg text-gray-700 mb-4">Simple examples kids already know include:</p>
              <ul className="list-disc list-inside text-lg text-gray-700 space-y-2 mb-6">
                <li>A Roomba vacuum that cleans your room.</li>
                <li>A robotic arm in factories that packs products.</li>
                <li>A line-following car that stays on track using sensors.</li>
              </ul>
            </motion.div>

            {/* Step 2 */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
              className="mb-12"
            >
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Step 2: Gather Your Materials</h2>
              <p className="text-lg text-gray-700 mb-6">
                You don't need expensive tools. Here's what's inside most beginner robotics kits:
              </p>
              <ul className="list-disc list-inside text-lg text-gray-700 space-y-2 mb-6">
                <li><strong>Battery Pack</strong> – to power your robot.</li>
                <li><strong>LEDs</strong> – for signals and lights.</li>
                <li><strong>Motors</strong> – to make it move.</li>
                <li><strong>Wires & Breadboard</strong> – to connect everything.</li>
                <li><strong>Microcontroller</strong> (like Arduino or Micro:bit) – the "brain" of the robot.</li>
                <li><strong>Laptop or Tablet</strong> – to write simple code that tells your robot what to do.</li>
              </ul>
              <p className="text-lg text-gray-700 italic">
                (All materials are included in our STEM Bears Robotics Kit.)
              </p>
            </motion.div>

            {/* Step 3 */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              viewport={{ once: true }}
              className="mb-12"
            >
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Step 3: Write the Brain Code</h2>
              <p className="text-lg text-gray-700 mb-6">
                Kids use easy drag-and-drop or block-based coding tools like Scratch or MakeCode to tell their robot what to do.
              </p>
              <p className="text-lg text-gray-700 mb-4">Example instructions:</p>
              <ul className="list-disc list-inside text-lg text-gray-700 space-y-2 mb-6">
                <li>If the light sensor sees darkness, turn on the headlight.</li>
                <li>If an obstacle is in front, turn right.</li>
              </ul>
              <p className="text-lg text-gray-700">
                This helps kids understand how computers make decisions — and makes programming fun and visual.
              </p>
            </motion.div>

            {/* Step 4 */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              viewport={{ once: true }}
              className="mb-12"
            >
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Step 4: Assemble and Test</h2>
              <p className="text-lg text-gray-700 mb-6">
                Put all the parts together — connect your wires, sensors, and motors. Start small: make the LED blink, then add motion. Test, fix, and try again — because real engineers learn by doing.
              </p>
            </motion.div>

            {/* Step 5 */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.5 }}
              viewport={{ once: true }}
              className="mb-12"
            >
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Step 5: Add Personality</h2>
              <p className="text-lg text-gray-700 mb-6">
                Now the fun part — customize your robot. Add stickers, name it, make it dance, or add a sound module that says "Hello." Every robot is unique — just like its creator.
              </p>
            </motion.div>

            {/* What Kids Learn */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.6 }}
              viewport={{ once: true }}
              className="mb-12"
            >
              <h2 className="text-3xl font-bold text-gray-900 mb-6">What Kids Learn from Building Robots</h2>
              <ul className="list-disc list-inside text-lg text-gray-700 space-y-2">
                <li><strong>Problem-solving</strong> — learning to debug and fix issues.</li>
                <li><strong>Teamwork</strong> — building and testing together.</li>
                <li><strong>Creativity</strong> — turning ideas into real, working models.</li>
                <li><strong>STEM Concepts</strong> — understanding electricity, sensors, and code.</li>
              </ul>
            </motion.div>

            {/* Quote Corner */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.7 }}
              viewport={{ once: true }}
              className="mb-12 bg-gradient-to-r from-blue-50 to-purple-50 p-8 rounded-2xl"
            >
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Quote Corner</h2>
              <div className="space-y-6">
                <blockquote className="text-lg text-gray-700 italic border-l-4 border-primary-600 pl-6">
                  "Everybody in this country should learn to program a computer, because it teaches you how to think."
                  <footer className="text-primary-600 font-semibold mt-2">— Steve Jobs, Co-founder of Apple</footer>
                </blockquote>
                <blockquote className="text-lg text-gray-700 italic border-l-4 border-primary-600 pl-6">
                  "Robotics and AI are the next big frontiers of human creativity."
                  <footer className="text-primary-600 font-semibold mt-2">— Sundar Pichai, CEO of Google</footer>
                </blockquote>
                <blockquote className="text-lg text-gray-700 italic border-l-4 border-primary-600 pl-6">
                  "The best way to predict the future is to invent it."
                  <footer className="text-primary-600 font-semibold mt-2">— Alan Kay, Computer Scientist</footer>
                </blockquote>
              </div>
            </motion.div>

            {/* Call to Action */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.8 }}
              viewport={{ once: true }}
              className="text-center bg-gradient-to-r from-primary-600 to-primary-700 text-white p-8 rounded-2xl"
            >
              <h2 className="text-3xl font-bold mb-4">Join the Robotics Adventure</h2>
              <p className="text-xl mb-6">
                At STEM Bears, our robotics program helps kids build, code, and bring their ideas to life — one project at a time.
              </p>
              <Link 
                to="/programs"
                className="inline-flex items-center bg-white text-primary-600 hover:bg-gray-50 font-bold text-lg px-8 py-3 rounded-xl transition-all duration-300 shadow-lg hover:shadow-xl"
              >
                Start Your Robotics Journey
              </Link>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  )
}

export default RoboticsBlogPage
